// Google Maps API utility functions

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface PlaceResult {
  address_components: AddressComponent[];
  formatted_address: string;
}

interface AddressFields {
  street: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Prediction {
  description: string;
  place_id: string;
}

// Load Google Maps API
export const loadGoogleMapsApi = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // If already loaded and properly initialized, resolve immediately
      if (window.google && window.google.maps && window.google.maps.Geocoder) {
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      // Define the callback function
      (window as any).initGoogleMaps = () => {
        // Verify the API is properly loaded with required components
        if (window.google && 
            window.google.maps && 
            window.google.maps.Geocoder && 
            window.google.maps.places) {
          console.log('✅ Google Maps API loaded successfully');
          resolve();
        } else {
          reject(new Error('Google Maps API did not load all required components'));
        }
        // Clean up the global callback
        delete (window as any).initGoogleMaps;
      };

      // Handle script error
      script.addEventListener('error', () => {
        const error = new Error('Failed to load Google Maps API script');
        console.error('❌ Google Maps API load failed');
        reject(error);
        // Clean up the global callback
        delete (window as any).initGoogleMaps;
      });

      // Add script to document
      document.head.appendChild(script);
    } catch (error) {
      console.error('❌ Error during Google Maps API initialization:', error);
      reject(error);
    }
  });
};

export const initGoogleMapsAutocomplete = async (
  inputElement: HTMLInputElement, 
  onSelect: (fields: AddressFields) => void,
  onPredictions?: (predictions: Prediction[]) => void
) => {
 
  
  // Ensure Google Maps API is loaded
  if (!window.google) {
    console.error('❌ Google Maps JavaScript API not loaded');
    return;
  }

  

  const autocomplete = new google.maps.places.Autocomplete(inputElement, {
    types: ['address'],
    componentRestrictions: { country: 'US' }
  });

  

  const placesService = new google.maps.places.PlacesService(document.createElement('div'));
  const searchService = new google.maps.places.AutocompleteService();

 
  // Handle input events for real-time predictions
  const handleInputChange = (e: Event) => {
    const query = (e.target as HTMLInputElement).value;
   
    
    if (query.length > 2 && onPredictions) {
      
      searchService.getPlacePredictions({
        input: query,
        types: ['address'],
        componentRestrictions: { country: 'US' }
      }, (predictions, status) => {
     
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
        
          const formattedPredictions = predictions.map(prediction => ({
            description: prediction.description,
            place_id: prediction.place_id
          }));
        
          onPredictions(formattedPredictions);
        } else {
      
          console.error('Error details:', predictions);
        }
      });
    } else {
     
     
    }
  };

  // Add both keyup and input event listeners for better coverage
  inputElement.addEventListener('keyup', handleInputChange);
  inputElement.addEventListener('input', handleInputChange);
 

  // Handle place selection
  autocomplete.addListener('place_changed', () => {
   
    const place = autocomplete.getPlace();
    if (!place.address_components) {
      console.error('❌ No address components found in selected place');
      return;
    }

   
    const addressFields = parseAddressComponents(place);
   
    onSelect(addressFields);
  });

  // Handle place selection by place_id
  const getPlaceDetails = (placeId: string) => {
   
    placesService.getDetails({
      placeId: placeId,
      fields: ['address_components', 'formatted_address']
    }, (place, status) => {
     
      if (status === google.maps.places.PlacesServiceStatus.OK && place && place.address_components) {
       
        const addressFields = parseAddressComponents(place as PlaceResult);
       
        onSelect(addressFields);
      } else {
        console.error('❌ Failed to get place details:', status);
      }
    });
  };

  return {
    getPlaceDetails
  };
};

const parseAddressComponents = (place: PlaceResult): AddressFields => {
 
  const addressFields: AddressFields = {
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  };

  // Extract street number and route (street name)
  let streetNumber = '';
  let streetName = '';

  place.address_components.forEach((component: AddressComponent) => {
    const type = component.types[0];
   

    switch (type) {
      case 'street_number':
        streetNumber = component.long_name;
        break;
      case 'route':
        streetName = component.long_name;
        break;
      case 'subpremise':
        addressFields.unit = component.long_name;
        break;
      case 'locality':
        addressFields.city = component.long_name;
        break;
      case 'administrative_area_level_1':
        addressFields.state = component.short_name;
        break;
      case 'postal_code':
        addressFields.zip = component.long_name;
        break;
      case 'country':
        addressFields.country = component.long_name;
        break;
    }
  });

  // Combine street number and name
  addressFields.street = `${streetNumber} ${streetName}`.trim();

  return addressFields;
};